import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
} from 'antd';

import {
  EMPTY_HIERARCHY,
  EMPTY_OBJECT_ID,
  HierarchyReference,
  ObjectReference,
} from '@totem/components/services/threats/configuration/types';
import AssigneeListInput from '@totem/components/ticketing/assignees/AssigneeListInput';
import {
  categoryOptions,
  subCategoryOptions,
  typeOptions,
} from '@totem/components/ticketing/assignments/categorizationOptions';
import MatchValueInput from '@totem/components/ticketing/assignments/MatchValueInput';
import TicketAssignmentsContext from '@totem/components/ticketing/assignments/TicketAssignmentsContext';
import {
  TICKET_ASSIGNMENT_PARTY_TYPES,
  TICKET_ASSIGNMENT_STATUS_OPTIONS,
  TicketAssignment,
} from '@totem/components/ticketing/assignments/types';
import OrgHierarchySelector from '@totem/components/ui_components/widgets/hierarchySelector/orgHierarchySelector';

const statusOptions = TICKET_ASSIGNMENT_STATUS_OPTIONS.map((status) => ({
  label: status,
  value: status,
}));

const defaultIdOrUndefined = (ref: ObjectReference): string | undefined =>
  ref?.id && ref.id !== EMPTY_OBJECT_ID ? ref.id : undefined;

const TicketAssignmentEditDrawer = () => {
  const {
    assignments,
    categorization,
    editingAssignment,
    drawerOpen,
    saving,
    onDrawerClose,
    onSave,
  } = useContext(TicketAssignmentsContext);
  const [form] = Form.useForm<TicketAssignment>();
  const [hierarchy, setHierarchy] =
    useState<HierarchyReference>(EMPTY_HIERARCHY);

  const isNew = !editingAssignment?.id;
  const selectedType: string | undefined = Form.useWatch(
    ['match', 'type'],
    form,
  );
  const selectedCategory: string | undefined = Form.useWatch(
    ['match', 'category'],
    form,
  );

  const typeSelectOptions = useMemo(
    () =>
      typeOptions(
        categorization,
        assignments.map((record) => record.match.type),
      ),
    [categorization, assignments],
  );
  const categorySelectOptions = useMemo(
    () =>
      categoryOptions(
        categorization,
        selectedType,
        assignments.map((record) => record.match.category),
      ),
    [categorization, selectedType, assignments],
  );
  const subCategorySelectOptions = useMemo(
    () =>
      subCategoryOptions(
        categorization,
        selectedType,
        selectedCategory,
        assignments.map((record) => record.match.subCategory),
      ),
    [categorization, selectedType, selectedCategory, assignments],
  );

  useEffect(() => {
    if (drawerOpen && editingAssignment) {
      form.setFieldsValue({
        ...editingAssignment,
        match: {
          ...editingAssignment.match,
          type: editingAssignment.match.type || undefined,
          category: editingAssignment.match.category || undefined,
          subCategory: editingAssignment.match.subCategory || undefined,
        },
        assignees: editingAssignment.assignees ?? [],
      });

      setHierarchy(editingAssignment.hierarchy ?? EMPTY_HIERARCHY);
    } else if (!drawerOpen) {
      form.resetFields();

      setHierarchy(EMPTY_HIERARCHY);
    }
  }, [drawerOpen, editingAssignment, form]);

  const scopeOrganizationName = defaultIdOrUndefined(hierarchy.organization)
    ? hierarchy.organization.name || 'the selected organization'
    : undefined;

  const handleHierarchyChanged = (
    organization: ObjectReference,
    region: ObjectReference,
    building: ObjectReference,
    controlSystem: ObjectReference,
  ) => {
    setHierarchy({ organization, region, building, controlSystem });
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      onSave({
        ...editingAssignment,
        ...values,
        hierarchy,
        match: {
          type: values.match?.type ?? '',
          category: values.match?.category ?? '',
          subCategory: values.match?.subCategory ?? '',
          summaryContains: values.match?.summaryContains ?? '',
        },
        assignees: (values.assignees ?? []).map((assignee) => ({
          partyType: assignee.partyType,
          type: assignee.type,
          value: (assignee.value ?? '').trim(),
          // Notify switch is hidden for now; default matches ticketing-library PARTY_INVOLVED_NOTIFY_DEFAULT
          notify: assignee.notify ?? true,
        })),
        notes: values.notes ?? '',
      });
    });
  };

  return (
    <Drawer
      title={isNew ? 'Add Ticket Assignment' : 'Edit Ticket Assignment'}
      open={drawerOpen}
      onClose={onDrawerClose}
      width={760}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onDrawerClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            {isNew ? 'Create' : 'Save'}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" size="small">
        <Card size="small" title="Basic Info" style={{ marginBottom: 16 }}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder="e.g. Deployment / Service Request / NOC" />
          </Form.Item>
          <Space style={{ width: '100%' }} size="middle">
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Required' }]}
              style={{ flex: 1, minWidth: 0 }}
            >
              <Select options={statusOptions} />
            </Form.Item>
            <Form.Item
              name="sequence"
              label="Sequence"
              tooltip="Entries are checked in ascending order; give more specific entries (e.g. with Summary Contains) a lower number than broad ones. Keep below 100 to run ahead of the generic ticket rules."
              rules={[{ required: true, message: 'Required' }]}
              style={{ flex: 1, minWidth: 0 }}
            >
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Space>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Card>

        <Card
          size="small"
          title="Scope"
          style={{ marginBottom: 16 }}
          extra={
            <Typography.Text type="secondary">
              Optional — leave unset for all organizations
            </Typography.Text>
          }
        >
          <OrgHierarchySelector
            key={editingAssignment?.id || 'new'}
            showOrganization
            showRegions={false}
            showBuildings
            showControlSystems={false}
            allowUnassignedOrganization
            allowUnassignedBuilding
            showLabels
            defaultOrganizationId={
              hierarchy.organization?.id || EMPTY_OBJECT_ID
            }
            defaultBuildingId={defaultIdOrUndefined(hierarchy.building)}
            onChange={handleHierarchyChanged}
          />
        </Card>

        <Card
          size="small"
          title="Match"
          style={{ marginBottom: 16 }}
          extra={
            <Typography.Text type="secondary">
              Blank fields match anything
            </Typography.Text>
          }
        >
          <Space style={{ width: '100%' }} size="middle" align="start">
            <Form.Item
              name={['match', 'type']}
              label="Ticket Type"
              style={{ flex: 1, minWidth: 0, width: 220 }}
            >
              <MatchValueInput options={typeSelectOptions} />
            </Form.Item>
            <Form.Item
              name={['match', 'category']}
              label="Category"
              style={{ flex: 1, minWidth: 0, width: 220 }}
            >
              <MatchValueInput options={categorySelectOptions} />
            </Form.Item>
            <Form.Item
              name={['match', 'subCategory']}
              label="Sub-Category"
              style={{ flex: 1, minWidth: 0, width: 220 }}
            >
              <MatchValueInput options={subCategorySelectOptions} />
            </Form.Item>
          </Space>
          <Form.Item
            name={['match', 'summaryContains']}
            label="Summary Contains"
            tooltip="Case-insensitive; matched as plain text, not a pattern."
          >
            <Input placeholder="e.g. NOC - Firewalls" />
          </Form.Item>
        </Card>

        <Card size="small" title="Assignees">
          <Typography.Paragraph type="secondary">
            If none of the Assignee rows resolve to anyone for a ticket, nothing
            is added and the next matching entry is tried.
          </Typography.Paragraph>
          <AssigneeListInput
            name="assignees"
            valueField="value"
            partyTypes={TICKET_ASSIGNMENT_PARTY_TYPES}
            addLabel="Add Person"
            scopeOrganizationName={scopeOrganizationName}
            unscopedReason="This entry's Scope is not limited to an organization"
          />
        </Card>
      </Form>
    </Drawer>
  );
};

export default TicketAssignmentEditDrawer;
